// Feasibility Score Display Component
// Shows urban renewal feasibility analysis results with visual scoring

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './FeasibilityScore.css';
import FeasibilityService from '../services/FeasibilityService';
import AIRecommendationService from '../services/AIRecommendationService';
import FormService from '../services/FormService';
import AnalyticsService from '../services/AnalyticsService';
import EligibilityCheck from './EligibilityCheck';
import AIRecommendation from './AIRecommendation';
import apiConfig from '../config/api';
import { buildUrbanixReportHtml } from '../utils/buildUrbanixReportHtml';

const FeasibilityScore = ({ searchResults, onFeasibilityCalculated, shouldCalculate = false, precalculatedData = null, isParentLoading = false, aiRecommendation, setAiRecommendation, aiAnalysisUnlocked, setAiAnalysisUnlocked, aiLoading, setAiLoading, aiError, setAiError, aiDisabledReason = '', onTabChange, onApplyFeasibilityOverride, feasibilityOverrideBusy = false }) => {
  const [feasibilityData, setFeasibilityData] = useState(precalculatedData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastCalculatedAddress, setLastCalculatedAddress] = useState(null);
  /** In-flight AI recommendation (started on CTA prefetch); verify flow awaits this. */
  const aiPrefetchPromiseRef = useRef(null);
  const aiPrefetchAddressRef = useRef(null);
  const aiAccessTokenRef = useRef(null);
  // Eligibility check uses pre-calculated feasibility data
  // AI state (aiRecommendation, aiLoading, aiError) is now managed by parent (ResultsPage)
  // so it survives tab switches

  // Update when precalculated data is received
  useEffect(() => {
    if (precalculatedData) {
      console.log('<i className="fi fi-rr-check"></i> Receiving precalculated feasibility data');
      setFeasibilityData(precalculatedData);
      setLoading(false);
    }
  }, [precalculatedData]);

  useEffect(() => {
    aiPrefetchPromiseRef.current = null;
    aiPrefetchAddressRef.current = null;
  }, [searchResults?.address]);

  useEffect(() => {
    console.log('🎯 FeasibilityScore useEffect triggered:', {
      address: searchResults?.address,
      lastCalculated: lastCalculatedAddress,
      loading: loading,
      shouldCalculate: shouldCalculate,
      hasPrecalculatedData: !!precalculatedData
    });
    
    // Skip calculation if we have precalculated data or if shouldCalculate is false
    if (precalculatedData || !shouldCalculate) {
      return;
    }
    
    // Only calculate if shouldCalculate is explicitly true
    // AND we have valid search results with address
    // AND we haven't already calculated for this address
    if (shouldCalculate === true && searchResults && searchResults.address && searchResults.address !== lastCalculatedAddress) {
      console.log('🚀 Starting new feasibility calculation (shouldCalculate=true)...');
      
      calculateFeasibility();
      
      // Scroll disabled when using pre-calculated data to avoid competing with ResultsPage scroll
    } else if (shouldCalculate === false) {
      console.log('⏸️ Feasibility calculation skipped - shouldCalculate is false');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCalculate]);

  // Scroll disabled when using pre-calculated data to avoid competing with ResultsPage scroll
  // Previously scrolled when feasibility data loaded, but now with pre-calculated data
  // this would conflict with the main results scroll

  const calculateFeasibility = async () => {
    if (loading || !searchResults?.address) return; // Prevent duplicate calls
    
    setLoading(true);
    setError(null);
    setLastCalculatedAddress(searchResults.address);
    
    try {
      console.log('🎯 Starting feasibility calculation for:', searchResults.address);
      console.log('🔍 Search results structure:', {
        address: searchResults.address,
        hasParcelData: !!searchResults.parcelData,
        hasBuildingData: !!searchResults.buildingData,
        hasPlans: !!searchResults.plans,
        hasCoordinates: !!searchResults.coordinates,
        municipality: searchResults.municipality,
        fullStructure: searchResults
      });
      
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Feasibility calculation timed out after 30 seconds')), 30000)
      );
      
      const calculationPromise = FeasibilityService.calculateFeasibilityScore(searchResults);
      
      const result = await Promise.race([calculationPromise, timeoutPromise]);
      
      console.log('<i className="fi fi-rr-stats"></i> Feasibility service result:', result);
      
      if (result.success) {
        console.log('<i className="fi fi-rr-check"></i> Setting feasibility data:', {
          hasResult: !!result,
          hasFeasibilityResult: !!result.feasibilityResult,
          score: result.feasibilityResult?.score0to100,
          subscores: result.feasibilityResult?.subscores
        });
        setFeasibilityData(result);
        console.log('<i className="fi fi-rr-check"></i> Feasibility data state updated');
        
        // AI recommendation is now triggered manually via button (cost control)
        // User must explicitly click "Generate AI Recommendation" button
        
        if (onFeasibilityCalculated) {
          onFeasibilityCalculated(result);
        }
      } else {
        console.error('❌ Feasibility calculation failed:', result.error);
        setError(result.error || 'שגיאה בחישוב הכדאיות');
      }
    } catch (err) {
      console.error('❌ Feasibility calculation error:', err);
      setError('שגיאה בחישוב הכדאיות: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const deliverAiReportToContact = async (feasibilityResult, contactInfo, recommendation) => {
    await FormService.submitForm('AI Recommendation Request', {
      name: contactInfo.name,
      email: contactInfo.email,
      address: searchResults?.address,
      city: searchResults?.parcelData?.municipalityName,
      feasibilityScore: feasibilityResult?.feasibilityResult?.score0to100
    });
    let printHtml = '';
    try {
      printHtml = buildUrbanixReportHtml(recommendation, feasibilityResult, searchResults);
    } catch (htmlErr) {
      console.error('buildUrbanixReportHtml:', htmlErr);
    }
    await fetch(`${apiConfig.apiBaseUrl}/api/contact?action=send-ai-report-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: contactInfo.email,
        name: contactInfo.name,
        address: searchResults?.address,
        recommendation,
        printHtml,
      }),
    });
  };

  const startAIPrefetch = useCallback(() => {
    if (aiDisabledReason) return;
    if (!feasibilityData || !searchResults?.address) return;
    if (aiRecommendation) return;
    if (aiPrefetchPromiseRef.current) return;

    setAiError(null);
    const addr = searchResults.address;
    aiPrefetchAddressRef.current = addr;

    const p = (async () => {
      // Warm prefetch only: prepare local payload and keep UX responsive.
      await new Promise((resolve) => setTimeout(resolve, 350));
      if (aiPrefetchAddressRef.current !== addr) return null;
      return null;
    })();

    aiPrefetchPromiseRef.current = p;
  }, [aiDisabledReason, feasibilityData, searchResults, aiRecommendation, setAiError]);

  const fetchAIRecommendation = async (feasibilityResult, contactInfo = null) => {
    if (aiDisabledReason) {
      setAiError(aiDisabledReason);
      return;
    }
    if (!feasibilityResult || !searchResults) return;

    if (contactInfo && aiRecommendation) {
      void deliverAiReportToContact(feasibilityResult, contactInfo, aiRecommendation).catch((err) =>
        console.error('Deliver AI report to contact:', err)
      );
      return;
    }

    if (contactInfo) {
      setAiLoading(true);
      setAiError(null);
      try {
        if (contactInfo.aiAccessToken) {
          aiAccessTokenRef.current = contactInfo.aiAccessToken;
        }

        let rec = aiRecommendation;
        if (!rec && aiPrefetchPromiseRef.current) {
          rec = await aiPrefetchPromiseRef.current;
        }
        if (!rec) {
          console.log('🤖 Fetching AI recommendation (no prefetch ready)...');
          AnalyticsService.trackAIRequestStarted({
            address: searchResults.address,
            municipality: searchResults?.municipality,
            sourceSurface: 'feasibility_unlock',
          });
          const result = await AIRecommendationService.getRecommendation(
            feasibilityResult,
            searchResults,
            searchResults.address,
            {
              aiAccessToken: aiAccessTokenRef.current,
              idempotencyKey: (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            }
          );
          if (!result.success) {
            setAiError(result.error || 'שגיאה ביצירת המלצת AI');
            AnalyticsService.trackAIRequestFailed({
              address: searchResults.address,
              municipality: searchResults?.municipality,
              sourceSurface: 'feasibility_unlock',
              errorCode: result.error || 'unknown_error',
            });
            return;
          }
          rec = result.recommendation;
          AnalyticsService.trackAIRequestSucceeded({
            address: searchResults.address,
            municipality: searchResults?.municipality,
            sourceSurface: 'feasibility_unlock',
          });
        }
        setAiRecommendation(rec);
        if (setAiAnalysisUnlocked) setAiAnalysisUnlocked(true);
        if (contactInfo.email && rec) {
          void deliverAiReportToContact(feasibilityResult, contactInfo, rec).catch((err) =>
            console.error('Deliver AI report to contact:', err)
          );
        }
      } catch (error) {
        console.error('❌ Error fetching AI recommendation:', error);
        setAiError('שגיאה ביצירת המלצת AI');
      } finally {
        setAiLoading(false);
      }
      return;
    }

    setAiLoading(true);
    setAiError(null);
    aiPrefetchPromiseRef.current = null;
    try {
      console.log('🤖 Fetching AI recommendation (retry / blocking)...');
      AnalyticsService.trackAIRequestStarted({
        address: searchResults?.address,
        municipality: searchResults?.municipality,
        sourceSurface: 'feasibility_retry',
      });
      const result = await AIRecommendationService.getRecommendation(
        feasibilityResult,
        searchResults,
        searchResults.address,
        {
          aiAccessToken: aiAccessTokenRef.current,
          idempotencyKey: (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        }
      );
      if (result.success) {
        setAiRecommendation(result.recommendation);
        if (setAiAnalysisUnlocked) setAiAnalysisUnlocked(false);
        AnalyticsService.trackAIRequestSucceeded({
          address: searchResults?.address,
          municipality: searchResults?.municipality,
          sourceSurface: 'feasibility_retry',
        });
      } else {
        console.error('❌ AI recommendation failed:', result.error);
        setAiError(result.error);
        AnalyticsService.trackAIRequestFailed({
          address: searchResults?.address,
          municipality: searchResults?.municipality,
          sourceSurface: 'feasibility_retry',
          errorCode: result.error || 'unknown_error',
        });
      }
    } catch (error) {
      console.error('❌ Error fetching AI recommendation:', error);
      setAiError('שגיאה ביצירת המלצת AI');
      AnalyticsService.trackAIRequestFailed({
        address: searchResults?.address,
        municipality: searchResults?.municipality,
        sourceSurface: 'feasibility_retry',
        errorCode: error?.message || 'unexpected_error',
      });
    } finally {
      setAiLoading(false);
    }
  };

  // Removed unused helper functions (no longer needed after UI simplification)

  console.log('🎯 FeasibilityScore render:', {
    loading,
    hasError: !!error,
    hasFeasibilityData: !!feasibilityData,
    feasibilityScore: feasibilityData?.feasibilityResult?.score0to100,
    hasSubscores: !!feasibilityData?.feasibilityResult?.subscores,
    shouldCalculate: shouldCalculate
  });

  // Show loading state when parent is loading and we don't have data yet
  if ((loading || isParentLoading) && !feasibilityData) {
    return (
      <div className="feasibility-section loading-feasibility">
        <div className="loading-spinner"></div>
        <p>מחשב כדאיות התחדשות עירונית...</p>
      </div>
    );
  }

  // Don't render anything if we don't have data and not loading
  if (!feasibilityData) {
    return null;
  }

  if (error) {
    return (
      <div className="feasibility-section">
        <div className="feasibility-error-message">
          <h3><i className="fi fi-rr-exclamation"></i> שגיאה בחישוב הכדאיות</h3>
          <p>{error}</p>
          <button 
            onClick={calculateFeasibility}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              marginTop: '12px'
            }}
          >
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  if (!feasibilityData) {
    console.log('<i className="fi fi-rr-exclamation"></i> No feasibility data, returning null');
    return null;
  }

  console.log('🎯 About to render feasibility results:', {
    feasibilityData: !!feasibilityData,
    hasFeasibilityResult: !!feasibilityData.feasibilityResult,
    score: feasibilityData.feasibilityResult?.score0to100,
    subscores: feasibilityData.feasibilityResult?.subscores,
    kpis: feasibilityData.feasibilityResult?.kpis
  });

  try {
    console.log('🎯 Rendering eligibility check with feasibility data');

    return (
    <>
      <div id="section-eligibility" className="feasibility-section">
        <EligibilityCheck
          feasibilityData={feasibilityData}
          onApplyFeasibilityOverride={onApplyFeasibilityOverride}
          feasibilityOverrideBusy={feasibilityOverrideBusy}
        />
      </div>
      <AIRecommendation
        sectionId="section-ai"
        recommendation={aiRecommendation}
        loading={aiLoading}
        error={aiError}
        onStartAiPrefetch={startAIPrefetch}
        onGenerateRecommendation={(contactInfo) => fetchAIRecommendation(feasibilityData, contactInfo)}
        feasibilityData={feasibilityData}
        searchResults={searchResults}
        onNavigateProjectPromotion={onTabChange ? () => onTabChange('project-promotion') : undefined}
        analysisUnlocked={aiAnalysisUnlocked}
        setAnalysisUnlocked={setAiAnalysisUnlocked}
        disabledReason={aiDisabledReason}
      />
    </>
  );
  } catch (error) {
    console.error('❌ Error rendering FeasibilityScore JSX:', error);
    return (
      <div className="feasibility-section">
        <div className="feasibility-error-message">
          <h3><i className="fi fi-rr-exclamation"></i> שגיאה בהצגת התוצאות</h3>
          <p>אירעה שגיאה בהצגת ניתוח הכדאיות: {error.message}</p>
        </div>
      </div>
    );
  }
};

export default FeasibilityScore;
